import React, { useState, useEffect } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Box,
  makeStyles,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import AttachmentIcon from "@material-ui/icons/Attachment";
import DeleteIcon from "@material-ui/icons/Delete";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import TableChartIcon from "@material-ui/icons/TableChart";
import ImageIcon from "@material-ui/icons/Image";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import GetAppIcon from "@material-ui/icons/GetApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import axios from "src/utils/axios";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  sectionLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: "#1b84ff",
    marginBottom: theme.spacing(2),
    display: "inline-block",
    position: "relative",
    "&::after": {
      content: '""',
      display: "block",
      width: 40,
      height: 3,
      background: "linear-gradient(90deg, #00B2FF, #0079FF)",
      borderRadius: 2,
      marginTop: 4,
    },
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: "#fff",
      transition: "all 0.2s ease-in-out",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4DA3FF",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1B84FF",
        boxShadow: "0 0 6px rgba(27,132,255,0.3)",
      },
    },
    "& .MuiInputBase-input": {
      paddingTop: 12,
      paddingBottom: 12,
    },
  },
  attachButton: {
    background: "linear-gradient(90deg,#00B2FF,#0079FF)",
    color: "#fff",
    borderRadius: 12,
    height: 44,
    width: 44,
    "&:hover": {
      opacity: 0.9,
    },
  },
  fileCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "8px 12px",
    background: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    marginBottom: 8,
  },
  fileHeader: {
    display: "flex",
    alignItems: "center",
    flex: 1,
  },
  fileActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
}));

// dosya tipine göre ikon
const getFileIcon = (fileName) => {
  const ext = String(fileName || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return <PictureAsPdfIcon style={{ color: "#e53e3e" }} />;
  if (["doc", "docx"].includes(ext))
    return <DescriptionIcon style={{ color: "#1b84ff" }} />;
  if (["xls", "xlsx", "csv"].includes(ext))
    return <TableChartIcon style={{ color: "#38a169" }} />;
  if (["jpg", "jpeg", "png", "gif"].includes(ext))
    return <ImageIcon style={{ color: "#805ad5" }} />;
  return <InsertDriveFileIcon style={{ color: "#718096" }} />;
};

// helper: file → base64 string
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]); // sadece base64 kısmı
    reader.onerror = (error) => reject(error);
  });

const createBlobUrl = (base64, mimeType) => {
  const normalizedBase64 = String(base64)
    .replace(/^data:[^;]+;base64,/, "")
    .replace(/\s/g, "");
  const byteCharacters = window.atob(normalizedBase64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024);
    const byteNumbers = new Array(slice.length);

    for (let index = 0; index < slice.length; index += 1) {
      byteNumbers[index] = slice.charCodeAt(index);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return URL.createObjectURL(
    new Blob(byteArrays, { type: mimeType || "application/octet-stream" })
  );
};

export default function AttachmentsBlock({ form, setForm, errors, loading }) {
  const classes = useStyles();
  const { t } = useTranslation("work_order");

  const [fileType, setFileType] = useState([]);
  const [DosyaTipi, setDosyaTipi] = useState(1);
  const [DosyaIsmi, setDosyaIsmi] = useState("");
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);

  const [localForm, setLocalForm] = useState({ files: [] });
    if (!form || !setForm) {
      form = localForm;
      setForm = setLocalForm;
    }

  useEffect(() => {
    axios
      .get(`/GetFileType`)
      .then((res) => setFileType(res.data?.length > 0 ? res.data : []))
      .catch(() => setFileType([]));
  }, []);

  const handleAttachClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;

      setIsProcessingFiles(true);
      try {
        const newFiles = [];

        for (const file of files) {
          const base64 = await toBase64(file);
          newFiles.push({
            file,
            name: DosyaIsmi || file.name,
            type: DosyaTipi,
            mimeType: file.type,
            base64,
            originalName: file.name,
            size: file.size,
            url: URL.createObjectURL(file),
          });
        }

        setForm((prev) => ({
          ...prev,
          files: [...(prev.files || []), ...newFiles],
        }));

        setDosyaIsmi("");
      } finally {
        setIsProcessingFiles(false);
      }
    };
    input.click();
  };

  const removeFile = (index) => {
    setForm((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const downloadFile = (file) => {
    if (!file.url) return;
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.originalName || file.name;
    link.click();
  };

  const openFile = (file) => {
    if (file.base64) {
      try {
        const blobUrl = createBlobUrl(file.base64, file.mimeType);
        window.open(blobUrl, "_blank");
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        return;
      } catch (error) {
        console.error("File preview error:", error);
      }
    }

    if (file.url) window.open(file.url, "_blank");
  };

  return (
    <Box mt={3}>
      <Typography className={classes.sectionLabel}>
        📎{t("attachments", "Attachments")}
      </Typography>
      {errors?.files && (
        <Typography variant="caption" style={{ color: "#e53e3e", display: "block", marginBottom: 8 }}>
          {errors.files}
        </Typography>
      )}

      <Grid container spacing={2} alignItems="center">
        {/* FileType */}
        <Grid item xs={12} sm={5} md={4}>
          <TextField
            className={classes.field}
            size="small"
            fullWidth
            select
            variant="outlined"
            label={t("DosyaTipi", "File Type")}
            value={DosyaTipi}
            onChange={(e) => setDosyaTipi(e.target.value)}
          >
            {fileType.map((type) => (
              <MenuItem key={type.Id} value={type.Id}>
                {type.FieldText}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* File Name */}
        <Grid item xs={12} sm={5} md={6}>
          <TextField
            className={classes.field}
            size="small"
            fullWidth
            variant="outlined"
            label={t("Dosya_ismi", "File Name")}
            value={DosyaIsmi}
            onChange={(e) => setDosyaIsmi(e.target.value)}
          />
        </Grid>

        {/* Attach Button */}
        <Grid item xs={12} sm={2} md={2}>
          <IconButton className={classes.attachButton} onClick={handleAttachClick}>
            <AttachmentIcon />
          </IconButton>
        </Grid>
      </Grid>

      {/* File List */}
      <Box
        style={{
          maxHeight: 120,
          overflowY: "auto",
          marginTop: 16,
        }}
      >
        {(loading || isProcessingFiles) ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ minHeight: 80, gap: 12 }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" style={{ color: "#4a5568" }}>
              {t("filesLoading", "Files are loading...")}
            </Typography>
          </Box>
        ) : form.files?.map((f, idx) => (
          <Box key={idx} className={classes.fileCard}>
            <Box className={classes.fileHeader}>
              {getFileIcon(f.originalName || f.name)}
              <Typography
                variant="body2"
                style={{ flex: 1, fontWeight: 500, marginLeft: 8 }}
              >
                {f.name}
              </Typography>
            </Box>

            <Box className={classes.fileActions}>
              <Typography
                variant="caption"
                style={{ marginRight: 12, color: "#4a5568" }}
              >
                {((f.size || 0) / 1024).toFixed(1)} KB
              </Typography>

              <Tooltip title={t("open", "Open")}>
                <IconButton
                  size="small"
                  onClick={() => openFile(f)}
                  style={{ background: "#edf2f7" }}
                >
                  <OpenInNewIcon fontSize="default" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("download", "Download")}>
                <IconButton
                  size="small"
                  onClick={() => downloadFile(f)}
                  style={{ background: "#edf2f7" }}
                >
                  <GetAppIcon fontSize="default" />
                </IconButton>
              </Tooltip>

              {!f.isPersisted && (
                <Tooltip title={t("delete", "Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => removeFile(idx)}
                    style={{ background: "#fed7d7", color: "#e53e3e" }}
                  >
                    <DeleteIcon fontSize="default" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
